import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  BookOpen,
  FileText,
  Video,
  Download,
  Calendar,
  User,
  Clock,
  ArrowRight,
  Search,
  Filter,
  TrendingUp,
  Users,
  BarChart3,
  Shield,
  Lightbulb,
  Target,
  Star,
  Play,
} from "lucide-react";

const ResourcesContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.gradients.background};
  padding-top: 6rem;
  overflow-x: hidden;
  width: 100%;
  position: relative;
`;

const HeroSection = styled.section`
  padding: 4rem 2rem 6rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  text-align: center;
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 3rem 1.5rem 5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 2.5rem 1rem 4rem;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1.5rem;
  line-height: 1.2;
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, ${({ theme }) => theme.typography.sizes.xl});
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  padding: 0 0.5rem;
`;

const SearchSection = styled.div`
  max-width: 600px;
  margin: 0 auto;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  background: ${({ theme }) => theme.colors.surface.primary};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent.primary};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FilterSection = styled.section`
  padding: 3rem 1rem;
  background: ${({ theme }) => theme.colors.gradients.subtle};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 2rem 0.75rem;
  }
`;

const FilterContainer = styled.div`
  max-width: ${({ theme }) => theme.container.xl};
  margin: 0 auto;
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterButton = styled(motion.button)<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  background: ${({ active, theme }) =>
    active ? theme.colors.gradients.primary : theme.colors.surface.primary};
  color: ${({ active, theme }) =>
    active ? theme.colors.text.inverse : theme.colors.text.secondary};
  border: 1px solid
    ${({ active, theme }) =>
      active ? "transparent" : theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:hover {
    background: ${({ active, theme }) =>
      active ? theme.colors.gradients.primary : theme.colors.surface.secondary};
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }
`;

const ContentSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const ContentContainer = styled.div`
  max-width: ${({ theme }) => theme.container.xl};
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 3rem;
  max-width: ${({ theme }) => theme.container.xl};
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    gap: 2rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 1.5rem;
  }
`;

const ResourceCard = styled(motion.article)`
  background: ${({ theme }) => theme.colors.surface.primary};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.card};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  width: 100%;
  max-width: 400px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.elevated};
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }
`;

const ResourceImage = styled.div<{ bgColor: string }>`
  height: 200px;
  background: ${({ bgColor }) => bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  color: ${({ theme }) => theme.colors.text.inverse};
`;

const ResourceBadge = styled.div<{
  type: "blog" | "whitepaper" | "webinar" | "guide";
}>`
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text.inverse};
  background: ${({ type, theme }) => {
    switch (type) {
      case "blog":
        return theme.colors.accent.primary;
      case "whitepaper":
        return "#10b981";
      case "webinar":
        return "#f59e0b";
      case "guide":
        return "#8b5cf6";
      default:
        return theme.colors.accent.primary;
    }
  }};
`;

const ResourceContent = styled.div`
  padding: 2rem;
`;

const ResourceMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ResourceTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.sizes.xl};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
  line-height: 1.3;
`;

const ResourceExcerpt = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ResourceTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Tag = styled.span`
  padding: 0.25rem 0.75rem;
  background: ${({ theme }) => theme.colors.surface.secondary};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
`;

const ResourceAction = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${({ theme }) => theme.colors.gradients.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const FeaturedSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.gradients.subtle};
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const FeaturedContainer = styled.div`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeaturedCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface.primary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.elevated};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 3rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FeaturedImage = styled.div<{ bgColor: string }>`
  height: 300px;
  background: ${({ bgColor }) => bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.inverse};
`;

const FeaturedContent = styled.div`
  padding: 2rem;
`;

const FeaturedTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.sizes["2xl"]};
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
`;

const FeaturedDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const NewsletterSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.gradients.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  text-align: center;
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const NewsletterContainer = styled.div`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
`;

const NewsletterTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  margin-bottom: 1rem;
`;

const NewsletterDescription = styled(motion.p)`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  margin-bottom: 3rem;
  opacity: 0.9;
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 1rem;
  max-width: 500px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 1rem;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  background: ${({ theme }) => theme.colors.surface.primary};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
  }
`;

const NewsletterButton = styled(motion.button)`
  padding: 1rem 2rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  color: ${({ theme }) => theme.colors.accent.primary};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const Resources: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const filters = [
    { id: "all", label: "All Resources" },
    { id: "blog", label: "Blog Posts" },
    { id: "whitepaper", label: "Whitepapers" },
    { id: "webinar", label: "Webinars" },
    { id: "guide", label: "Guides" },
  ];

  const resources = [
    {
      id: "data-transformation-guide",
      type: "guide" as const,
      title: "The Complete Guide to Educational Data Transformation",
      excerpt:
        "A comprehensive 50-page guide on turning complex student data into engaging, actionable insights that drive learning outcomes.",
      author: "Dr. Sarah Johnson",
      date: "2024-01-15",
      readTime: "15 min read",
      tags: ["Data Analytics", "Best Practices", "Implementation"],
      bgColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    },
    {
      id: "ferpa-compliance-blog",
      type: "blog" as const,
      title: "FERPA Compliance in the Age of Social Learning Platforms",
      excerpt:
        "Understanding how to maintain student privacy while creating engaging social media-style educational experiences.",
      author: "Michael Chen",
      date: "2024-01-10",
      readTime: "8 min read",
      tags: ["FERPA", "Privacy", "Compliance", "Legal"],
      bgColor: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    },
    {
      id: "engagement-metrics-webinar",
      type: "webinar" as const,
      title: "Measuring Student Engagement: Beyond Traditional Metrics",
      excerpt:
        "Join our expert panel discussing innovative ways to measure and improve student engagement in digital learning environments.",
      author: "Swintek Team",
      date: "2024-01-08",
      readTime: "45 min watch",
      tags: ["Engagement", "Metrics", "Analytics", "Webinar"],
      bgColor: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    },
    {
      id: "mobile-first-whitepaper",
      type: "whitepaper" as const,
      title: "Mobile-First Educational Platforms: Research & Implementation",
      excerpt:
        "Research-backed strategies for implementing mobile-first educational technologies that students actually want to use.",
      author: "Dr. Emily Rodriguez",
      date: "2024-01-05",
      readTime: "25 min read",
      tags: ["Mobile", "Research", "Implementation", "UX"],
      bgColor: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    },
    {
      id: "gamification-strategies",
      type: "blog" as const,
      title: "Gamification Strategies That Actually Work in Education",
      excerpt:
        "Moving beyond badges and points to create meaningful gamified experiences that enhance learning outcomes.",
      author: "Alex Thompson",
      date: "2024-01-03",
      readTime: "12 min read",
      tags: ["Gamification", "Engagement", "Psychology", "Design"],
      bgColor: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
    },
    {
      id: "ai-tutoring-future",
      type: "whitepaper" as const,
      title: "The Future of AI-Powered Personalized Learning",
      excerpt:
        "Exploring how artificial intelligence is reshaping personalized education and what it means for educators and students.",
      author: "Dr. James Park",
      date: "2023-12-28",
      readTime: "30 min read",
      tags: ["AI", "Personalization", "Future", "Technology"],
      bgColor: "linear-gradient(135deg, #96fbc4 0%, #f9f586 100%)",
    },
  ];

  const featuredResources = [
    {
      title: "2024 State of Educational Technology Report",
      description:
        "Our comprehensive annual report analyzing trends, challenges, and opportunities in educational technology across 200+ school districts.",
      bgColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      type: "Download Report",
    },
    {
      title: "Interactive Demo: Social Learning Platform",
      description:
        "Experience our award-winning social learning platform firsthand with this interactive demo featuring real student data scenarios.",
      bgColor: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      type: "Try Demo",
    },
  ];

  const filteredResources = resources.filter((resource) => {
    const matchesFilter =
      activeFilter === "all" || resource.type === activeFilter;
    const matchesSearch =
      searchTerm === "" ||
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );

    return matchesFilter && matchesSearch;
  });

  const getResourceIcon = (type: string) => {
    switch (type) {
      case "blog":
        return <BookOpen size={40} />;
      case "whitepaper":
        return <FileText size={40} />;
      case "webinar":
        return <Video size={40} />;
      case "guide":
        return <Lightbulb size={40} />;
      default:
        return <BookOpen size={40} />;
    }
  };

  const getActionText = (type: string) => {
    switch (type) {
      case "blog":
        return "Read Article";
      case "whitepaper":
        return "Download PDF";
      case "webinar":
        return "Watch Recording";
      case "guide":
        return "Download Guide";
      default:
        return "Read More";
    }
  };

  return (
    <ResourcesContainer>
      <HeroSection>
        <HeroContent
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Educational Technology Resources
          </HeroTitle>

          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Insights, research, and practical guides to help educators leverage
            technology for better learning outcomes.
          </HeroSubtitle>

          <SearchSection>
            <SearchIcon size={20} />
            <SearchInput
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchSection>
        </HeroContent>
      </HeroSection>

      <FilterSection>
        <FilterContainer>
          {filters.map((filter) => (
            <FilterButton
              key={filter.id}
              active={activeFilter === filter.id}
              onClick={() => setActiveFilter(filter.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {filter.label}
            </FilterButton>
          ))}
        </FilterContainer>
      </FilterSection>

      <FeaturedSection>
        <FeaturedContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Featured Resources
          </SectionTitle>

          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Our most popular and impactful educational technology resources
          </SectionSubtitle>

          {featuredResources.map((resource, index) => (
            <FeaturedCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <FeaturedImage bgColor={resource.bgColor}>
                <TrendingUp size={80} />
              </FeaturedImage>
              <FeaturedContent>
                <FeaturedTitle>{resource.title}</FeaturedTitle>
                <FeaturedDescription>
                  {resource.description}
                </FeaturedDescription>
                <ResourceAction
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Download size={20} />
                  {resource.type}
                </ResourceAction>
              </FeaturedContent>
            </FeaturedCard>
          ))}
        </FeaturedContainer>
      </FeaturedSection>

      <ContentSection>
        <ContentContainer>
          <ContentGrid>
            {filteredResources.map((resource, index) => (
              <ResourceCard
                key={resource.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                layout
              >
                <ResourceImage bgColor={resource.bgColor}>
                  <ResourceBadge type={resource.type}>
                    {resource.type.charAt(0).toUpperCase() +
                      resource.type.slice(1)}
                  </ResourceBadge>
                  {getResourceIcon(resource.type)}
                </ResourceImage>

                <ResourceContent>
                  <ResourceMeta>
                    <MetaItem>
                      <User size={14} />
                      {resource.author}
                    </MetaItem>
                    <MetaItem>
                      <Calendar size={14} />
                      {new Date(resource.date).toLocaleDateString()}
                    </MetaItem>
                    <MetaItem>
                      <Clock size={14} />
                      {resource.readTime}
                    </MetaItem>
                  </ResourceMeta>

                  <ResourceTitle>{resource.title}</ResourceTitle>
                  <ResourceExcerpt>{resource.excerpt}</ResourceExcerpt>

                  <ResourceTags>
                    {resource.tags.map((tag, idx) => (
                      <Tag key={idx}>{tag}</Tag>
                    ))}
                  </ResourceTags>

                  <ResourceAction
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {resource.type === "webinar" ? (
                      <Play size={16} />
                    ) : (
                      <ArrowRight size={16} />
                    )}
                    {getActionText(resource.type)}
                  </ResourceAction>
                </ResourceContent>
              </ResourceCard>
            ))}
          </ContentGrid>
        </ContentContainer>
      </ContentSection>

      <NewsletterSection>
        <NewsletterContainer>
          <NewsletterTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Stay Updated with Educational Technology Trends
          </NewsletterTitle>

          <NewsletterDescription
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Get the latest insights, research, and practical guides delivered to
            your inbox monthly.
          </NewsletterDescription>

          <NewsletterForm>
            <NewsletterInput
              type="email"
              placeholder="Enter your email address"
              required
            />
            <NewsletterButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Subscribe
            </NewsletterButton>
          </NewsletterForm>
        </NewsletterContainer>
      </NewsletterSection>
    </ResourcesContainer>
  );
};

export default Resources;
